import { PLAYER_COLORS } from "@/lib/api";
import Coin from "@/components/game/Coin";
import { squareText } from "@/lib/squares";

const HINTS = {
  sabotage: { choose_target: "Choose an opponent's Sea Wall (the whole stack breaks)" },
  relocated: { choose_source: "Choose one of your squares to move", choose_destination: "Choose an empty square" },
  board_thief: { choose_target: "Choose an opponent's square to take", choose_destination: "Choose one of your squares to give" },
  repair_card: { choose_target_wall: "Choose your broken Sea Wall" },
};

const TARGET_LABELS = {
  sabotage: ["Wall"],
  relocated: ["Move", "To"],
  board_thief: ["Take", "Give"],
  repair_card: ["Wall"],
};

const textStyle = { fontSize: 12.5, lineHeight: "16px", color: "var(--ink-2)" };

// Takes the card buttons' place under the SELECTED CARD while an action card is in progress:
// card name + "Already paid" (PAY UPFRONT: paid 3 when taken), step hint / chosen targets /
// result preview, then Back · Cancel · Confirm.
export default function ActionCardPanel({ state, cells, gridRows, busy, onBack, onCancel, onConfirm }) {
  const { card_key, card_name, step, payload = {} } = state;
  const isConfirm = step === "confirm";
  const chosen = payload.chosen || [];
  const labels = TARGET_LABELS[card_key] || [];
  const canBack = (payload.history || []).length > 0;
  const hint = HINTS[card_key]?.[step] || "Choose a highlighted square";

  const buttons = [
    canBack && { testid: "action-card-back", label: "Back", onClick: onBack, cls: "btn-secondary" },
    { testid: "action-card-cancel", label: "Cancel", onClick: onCancel, cls: "btn-secondary" },
    isConfirm && { testid: "action-card-confirm", label: "Confirm", onClick: onConfirm, cls: "btn-primary" },
  ].filter(Boolean);

  // Under the SELECTED CARD (narrow left column): the step text on top, the buttons in one row
  // under it with short labels that always fit. The card's name is on the large card above;
  // here it may wrap but is never cut.
  return (
    <div data-testid="action-card-panel" data-step={step} className="w-full flex flex-col min-h-0" style={{ gap: 6 }}>
      <div className="flex items-baseline justify-between min-w-0" style={{ gap: 8 }}>
        <div data-testid="action-card-name" className="min-w-0" style={{ fontSize: 14, fontWeight: 700, lineHeight: "17px", color: "var(--ink)", overflowWrap: "anywhere" }}>{card_name}</div>
        <div data-testid="action-card-cost" className="shrink-0 inline-flex items-center gap-1 whitespace-nowrap" style={{ fontSize: 12, lineHeight: "16px", color: "var(--ink-3)" }}>
          <Coin size={13} />
          Already paid
        </div>
      </div>
      <div className="flex flex-col min-w-0 min-h-0 overflow-hidden" style={{ ...textStyle, gap: 2 }}>
        {!isConfirm && <span data-testid="action-hint-text" style={{ fontWeight: 600 }}>{hint}</span>}
        {chosen.length > 0 && (
          <span data-testid="action-card-targets" className="flex flex-col" style={{ color: "var(--ink-3)", gap: 1 }}>
            {chosen.map((k, i) => (
              <span key={k} className="truncate">
                <span style={{ fontWeight: 600, color: "var(--ink-2)" }}>{labels[i] || "Target"}:</span> {cells[k]?.card_name || "empty square"} ({squareText(k, gridRows)})
                {cells[k]?.owner && <span className="inline-block w-2.5 h-2.5 ml-1 align-middle" style={{ borderRadius: 3, background: PLAYER_COLORS[cells[k].owner] }} />}
              </span>
            ))}
          </span>
        )}
        {isConfirm && (
          <span data-testid="action-card-result" className="line-clamp-4" title={(payload.preview?.result || []).join(" ")}>
            {(payload.preview?.result || []).map((line, i) => (
              <span key={i} data-testid={`action-result-line-${i}`}>{i > 0 ? " " : ""}{line}</span>
            ))}
          </span>
        )}
      </div>
      <div className="shrink-0 grid w-full" style={{ gridTemplateColumns: `repeat(${buttons.length}, minmax(0, 1fr))`, columnGap: 6 }}>
        {buttons.map((b) => (
          <button key={b.testid} type="button" data-testid={b.testid} onClick={b.onClick} disabled={busy} className={`${b.cls} btn-compact btn-card w-full`} style={{ height: 34, padding: "0 6px", fontSize: 12.5, whiteSpace: "nowrap" }}>
            {b.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// Online, other screens: the card stays hidden, only who is playing one.
export function ActionCardWaiting({ name }) {
  return (
    <div data-testid="action-card-waiting" className="w-full" style={{ ...textStyle, fontSize: 13 }}>
      {name} is playing an action card…
    </div>
  );
}

// Shown for 3 seconds after Confirm, before the draw step.
export function ActionResultFlash({ result }) {
  return (
    <div data-testid="action-result-flash" className="w-full flex flex-col animate-in fade-in duration-200" style={{ gap: 4 }}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="truncate" style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)" }}>{result.card_name}</div>
        <span className="shrink-0 inline-flex items-center gap-1 whitespace-nowrap" style={{ fontSize: 12.5, fontWeight: 600, color: "var(--sea)" }}>
          <Coin size={14} />
          {result.cost ? `Paid ${result.cost} money` : "Already paid"}
        </span>
      </div>
      <div className="line-clamp-4" style={textStyle} title={result.lines.join(" ")}>
        {result.lines.map((l, i) => <span key={i}>{i > 0 ? " " : ""}{l}</span>)}
      </div>
    </div>
  );
}
